import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, finalize } from 'rxjs/operators';

import { ConfigService } from '../../_services/config.service';
import { SpinnerService } from '../../_services/SpinnerService';
import { UserNotify, UserNotifyType, NotificationFilter, NotifyBatchRequest } from '../_models/notification.model';

@Injectable({
	providedIn: 'root',
})
export class NotificationsService {
	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private spinnerService: SpinnerService,
	) {}

	private get baseUrl(): string {
		return this.configService.portalUrl;
	}

	private formatDateParam(value: string | Date | undefined): string | null {
		if (!value) return null;
		if (value instanceof Date) {
			const y = value.getFullYear();
			const m = String(value.getMonth() + 1).padStart(2, '0');
			const d = String(value.getDate()).padStart(2, '0');
			return `${y}-${m}-${d}`;
		}
		return value;
	}

	GetNotifications(txtsearch: string = '', order_by: string = '', limit: number = 20, offset: number = 0, filters?: NotificationFilter): Observable<UserNotify[]> {
		this.spinnerService.show();

		let url = `${this.baseUrl}notify/list?txtsearch=${encodeURIComponent(txtsearch)}&orderby=${encodeURIComponent(order_by)}&limit=${limit}&offset=${offset}`;

		if (filters) {
			if (filters.usrnot_type) url += `&usrnot_type=${filters.usrnot_type}`;
			if (filters.usrnot_typesent) url += `&usrnot_typesent=${filters.usrnot_typesent}`;
			if (filters.status) url += `&status=${filters.status}`;
			const df = this.formatDateParam(filters.date_from);
			if (df) url += `&date_from=${df}`;
			const dt = this.formatDateParam(filters.date_to);
			if (dt) url += `&date_to=${dt}`;
		}

		return this.http.get(url, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotification(usrnot_id: number): Observable<UserNotify> {
		this.spinnerService.show();

		return this.http.get(`${this.baseUrl}notify/get/${usrnot_id}`, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	InsertNotification(notification: Partial<UserNotify>): Observable<number> {
		this.spinnerService.show();

		return this.http.post(`${this.baseUrl}notify/add`, notification, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	DeleteNotification(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.delete(`${this.baseUrl}notify/del/${usrnot_id}`, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	MarkAsRead(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/mark-read/${usrnot_id}`, {}, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	UpdateNotification(notification: Partial<UserNotify>): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/upd`, notification, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotifyTypes(): Observable<UserNotifyType[]> {
		return this.http.get(`${this.baseUrl}notify/types`, { observe: 'response' }).pipe(map((response: any) => response.body));
	}

	GetUsersForNotify(fcmOnly: boolean = false): Observable<any[]> {
		const params = fcmOnly ? 'txtsearch=&fcm_only=true' : 'txtsearch=';
		return this.http.get(`${this.baseUrl}notify/users?${params}`, { observe: 'response' }).pipe(map((response: any) => response.body));
	}

	GetUnreadCount(): Observable<number> {
		return this.http.get(`${this.baseUrl}notify/unread-count`, { observe: 'response' }).pipe(map((response: any) => response.body));
	}

	RetryNotification(usrnot_id: number): Observable<number> {
		this.spinnerService.show();

		return this.http.put(`${this.baseUrl}notify/retry/${usrnot_id}`, {}, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	InsertBatchNotification(request: NotifyBatchRequest): Observable<number> {
		this.spinnerService.show();

		return this.http.post(`${this.baseUrl}notify/add-batch`, request, { observe: 'response' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	UploadNotificationImage(file: File): Observable<string> {
		this.spinnerService.show();
		const formData = new FormData();
		formData.append('file', file);

		return this.http.post(`${this.baseUrl}notify/upload-image`, formData, { observe: 'response', responseType: 'text' }).pipe(
			map((response: any) => response.body),
			finalize(() => this.spinnerService.hide()),
		);
	}

	GetNotificationImageUrl(filename: string): string {
		return `${this.baseUrl}notify/image/${filename}`;
	}
}
